import { CustomReward, Family } from "@joonapp/web-shared"
import { useMutation, useQuery } from "@tanstack/react-query"
import { User } from "firebase/auth"
import toast from "react-hot-toast"

import {
  createCustomReward,
  createScreenTimeManualException,
  createScreenTimeScheduledException,
  deleteCustomReward,
  deleteScreenTimeManualException,
  deleteScreenTimeScheduledException,
  editCustomReward,
  endScreenTimeSession,
  getScreenTimeActiveSession,
  purchaseCustomReward,
  purchaseShopItem,
  redeemCustomReward,
  undoCustomRewardLog,
  updateChildAssignment,
  updateScreenTimeScheduledException,
} from "./api"
import { QUERY_KEYS } from "../../constants"
import useMarkMissionComplete from "../../hooks/useMarkMissionComplete"
import { FamilyQuerySelect, MissionScene } from "../../types"
import { ScreenTimeLog } from "../../types/rewards"
import { queryClient } from "../../util/queryClient"
import { useFamilyQuery } from "../queries"
import { editParentPanelPasscode, editPasscode } from "../users/api"

export const useEditRewardMutation = () =>
  useMutation({
    mutationFn: editCustomReward,
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.CUSTOM_REWARDS])
      queryClient.invalidateQueries([QUERY_KEYS.CUSTOM_REWARD_LOGS])
      queryClient.invalidateQueries([QUERY_KEYS.CUSTOM_REWARD_BALANCES])
      queryClient.invalidateQueries([QUERY_KEYS.SCREEN_TIME_BALANCES])
      toast.success("Reward updated successfully!")
    },
  })

export const useAddRewardMutation = () => {
  const markMissionComplete = useMarkMissionComplete(MissionScene.REWARDS_TAB)

  return useMutation({
    mutationFn: createCustomReward,
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.CUSTOM_REWARD_LOGS])
      queryClient.invalidateQueries([QUERY_KEYS.CUSTOM_REWARD_BALANCES])
      queryClient.invalidateQueries([QUERY_KEYS.SCREEN_TIME_BALANCES])
      queryClient.invalidateQueries([QUERY_KEYS.CUSTOM_REWARDS])
      markMissionComplete()
    },
  })
}

export const usePurchaseRewardMutation = () =>
  useMutation({
    mutationFn: purchaseCustomReward,
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.CUSTOM_REWARD_LOGS])
      queryClient.invalidateQueries([QUERY_KEYS.CUSTOM_REWARD_BALANCES])
      queryClient.invalidateQueries([QUERY_KEYS.SCREEN_TIME_BALANCES])
      queryClient.invalidateQueries([QUERY_KEYS.CUSTOM_REWARDS])
      queryClient.invalidateQueries([QUERY_KEYS.FAMILY])
      queryClient.invalidateQueries([QUERY_KEYS.COIN_CUMULATIVE_STATS])
    },
  })

export const useRedeemRewardMutation = () =>
  useMutation({
    mutationFn: redeemCustomReward,
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.CUSTOM_REWARD_LOGS])
      queryClient.invalidateQueries([QUERY_KEYS.CUSTOM_REWARD_BALANCES])
      queryClient.invalidateQueries([QUERY_KEYS.SCREEN_TIME_BALANCES])
    },
  })

export const useDeleteRewardMutation = () =>
  useMutation({
    mutationFn: deleteCustomReward,
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.CUSTOM_REWARDS])
      queryClient.invalidateQueries([QUERY_KEYS.CUSTOM_REWARD_BALANCES])
      queryClient.invalidateQueries([QUERY_KEYS.SCREEN_TIME_BALANCES])
      queryClient.invalidateQueries([QUERY_KEYS.CUSTOM_REWARD_LOGS])
      queryClient.invalidateQueries([QUERY_KEYS.FAMILY])
      toast.success("Reward deleted successfully!")
    },
  })

export const useUndoCustomRewardLogMutation = () =>
  useMutation({
    mutationFn: undoCustomRewardLog,
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.CUSTOM_REWARD_LOGS])
      queryClient.invalidateQueries([QUERY_KEYS.CUSTOM_REWARD_BALANCES])
      queryClient.invalidateQueries([QUERY_KEYS.SCREEN_TIME_BALANCES])
      queryClient.invalidateQueries([QUERY_KEYS.CUSTOM_REWARDS])
      queryClient.invalidateQueries([QUERY_KEYS.FAMILY])
    },
  })

export const useEditPasscodeMutation = () =>
  useMutation({
    mutationFn: editPasscode,
    onMutate: async ({ userId, accountPasscode }) => {
      await queryClient.cancelQueries([QUERY_KEYS.FAMILY])
      await queryClient.cancelQueries([QUERY_KEYS.USER])
      const previousFamily = queryClient.getQueryData([
        QUERY_KEYS.FAMILY,
      ]) as Family
      const previousUser = queryClient.getQueryData<User>([QUERY_KEYS.USER])

      queryClient.setQueryData([QUERY_KEYS.FAMILY], {
        ...previousFamily,
        profiles: previousFamily?.profiles?.map((profile) =>
          profile.user.id === userId
            ? {
                ...profile,
                user: { ...profile.user, account_passcode: accountPasscode },
              }
            : profile
        ),
      })
      queryClient.setQueryData([QUERY_KEYS.USER], {
        ...previousUser,
        account_passcode: accountPasscode,
      })
      return { previousFamily, previousUser }
    },
    onError: (_error, _variables, context) => {
      queryClient.setQueryData([QUERY_KEYS.FAMILY], context?.previousFamily)
      queryClient.setQueryData([QUERY_KEYS.USER], context?.previousUser)
    },
    onSettled: () => {
      queryClient.invalidateQueries([QUERY_KEYS.USER])
      queryClient.invalidateQueries([QUERY_KEYS.FAMILY])
    },
  })

export const useEditParentPanelPasscodeMutation = () =>
  useMutation({
    mutationFn: editParentPanelPasscode,
    onMutate: async ({ parentPanelPasscode }) => {
      await queryClient.cancelQueries([QUERY_KEYS.USER])
      const previousUser = queryClient.getQueryData<User>([QUERY_KEYS.USER])
      queryClient.setQueryData([QUERY_KEYS.USER], {
        ...previousUser,
        parent_panel_passcode: parentPanelPasscode,
      })
      return { previousUser }
    },
    onError: (_error, _variables, context) => {
      queryClient.setQueryData([QUERY_KEYS.USER], context?.previousUser)
    },
    onSettled: () => {
      queryClient.invalidateQueries([QUERY_KEYS.USER])
    },
  })

export const usePurchaseShopItemMutation = () =>
  useMutation({
    mutationFn: purchaseShopItem,
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.INVENTORY])
      queryClient.invalidateQueries([QUERY_KEYS.PARENT_SHOP])
      queryClient.invalidateQueries([QUERY_KEYS.USER])
      queryClient.invalidateQueries([QUERY_KEYS.FAMILY])
      queryClient.invalidateQueries([QUERY_KEYS.COIN_CUMULATIVE_STATS])
    },
  })

export const useUpdateChildAssignmentMutation = () =>
  useMutation({
    mutationFn: updateChildAssignment,
    onMutate: async ({ assignmentId, bypassRedeem }) => {
      await queryClient.cancelQueries([QUERY_KEYS.CUSTOM_REWARDS])

      const previousRewards = queryClient.getQueryData<CustomReward[]>([
        QUERY_KEYS.CUSTOM_REWARDS,
      ])

      const newRewards = previousRewards?.map((reward) => {
        const updatedAssignments = reward.assignments?.map((assignment) => {
          if (assignment.id === assignmentId) {
            return { ...assignment, bypass_redeem: bypassRedeem }
          }
          return assignment
        })
        return {
          ...reward,
          assignments: updatedAssignments,
        }
      })

      queryClient.setQueryData([QUERY_KEYS.CUSTOM_REWARDS], newRewards)
      return { previousRewards }
    },
    onError: (_error, _variables, context) => {
      queryClient.setQueryData(
        [QUERY_KEYS.CUSTOM_REWARDS],
        context?.previousRewards
      )
    },
    onSettled: () => {
      queryClient.invalidateQueries([QUERY_KEYS.CUSTOM_REWARDS])
      queryClient.invalidateQueries([QUERY_KEYS.CUSTOM_REWARD_BALANCES])
      queryClient.invalidateQueries([QUERY_KEYS.SCREEN_TIME_BALANCES])
    },
  })

export const useCreateScreenTimeScheduledExceptionMutation = () =>
  useMutation({
    mutationFn: createScreenTimeScheduledException,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries([
          QUERY_KEYS.SCREEN_TIME_CURRENT_EXCEPTION,
        ]),
        queryClient.invalidateQueries([
          QUERY_KEYS.SCREEN_TIME_SCHEDULED_EXCEPTIONS,
        ]),
      ])
    },
  })

export const useUpdateScreenTimeScheduledExceptionMutation = () =>
  useMutation({
    mutationFn: updateScreenTimeScheduledException,
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.SCREEN_TIME_CURRENT_EXCEPTION])
      queryClient.invalidateQueries([
        QUERY_KEYS.SCREEN_TIME_SCHEDULED_EXCEPTIONS,
      ])
    },
  })

export const useDeleteScreenTimeScheduledExceptionMutation = () =>
  useMutation({
    mutationFn: deleteScreenTimeScheduledException,
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.SCREEN_TIME_CURRENT_EXCEPTION])
      queryClient.invalidateQueries([
        QUERY_KEYS.SCREEN_TIME_SCHEDULED_EXCEPTIONS,
      ])
    },
  })

export const useCreateScreenTimeManualExceptionMutation = () =>
  useMutation({
    mutationFn: createScreenTimeManualException,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries([
          QUERY_KEYS.SCREEN_TIME_CURRENT_EXCEPTION,
        ]),
        queryClient.invalidateQueries([
          QUERY_KEYS.SCREEN_TIME_MANUAL_EXCEPTIONS,
        ]),
      ])
    },
  })

export const useDeleteScreenTimeManualExceptionMutation = () =>
  useMutation({
    mutationFn: deleteScreenTimeManualException,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries([
          QUERY_KEYS.SCREEN_TIME_CURRENT_EXCEPTION,
        ]),
        queryClient.invalidateQueries([
          QUERY_KEYS.SCREEN_TIME_MANUAL_EXCEPTIONS,
        ]),
      ])
    },
  })

export const useGetActiveScreenTimeSessionsQuery = () => {
  const { data: children } = useFamilyQuery(FamilyQuerySelect.CHILDREN)
  const childIds = children?.map((child) => child.user.id)
  return useQuery({
    queryKey: [QUERY_KEYS.SCREEN_TIME_ACTIVE_SESSION],
    queryFn: () =>
      Promise.all(
        childIds?.map((userId) => getScreenTimeActiveSession({ userId })) || []
      ),
    select: (data) =>
      data.filter((session) => typeof session === "object") as
        | ScreenTimeLog[]
        | undefined,
    enabled: !!childIds,
  })
}

export const useEndScreenTimeSessionMutation = () =>
  useMutation({
    mutationFn: endScreenTimeSession,
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.SCREEN_TIME_ACTIVE_SESSION])
      queryClient.invalidateQueries([QUERY_KEYS.SCREEN_TIME_BALANCES])
      queryClient.invalidateQueries([QUERY_KEYS.CUSTOM_REWARD_BALANCES])
      toast.success("Screen time session ended")
    },
    onError: () => {
      toast.error("Failed to end screen time session")
    },
  })
